import {
  effectiveConversationUnreadCount,
  type MappedConversation,
} from "@/lib/conversation-list-map";

/** Sincroniza badges da sidebar/header após patch otimista na lista. */
export const CONVERSATION_INBOX_PATCH_EVENT = "conversation-inbox-badges-sync";
export const CONVERSATION_INBOX_ROW_PATCH_EVENT = "conversation-inbox-row-patch";

export type ConversationInboxRowPatch = {
  conversationId: string;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  lastMessageDirection?: string | null;
  unreadCount?: number;
  updatedAt?: string;
  state?: string;
  isClosed?: boolean;
  escalatedByAgentAt?: string | null;
  externalUserId?: string | null;
  version?: number;
};

function normalizePreviewBody(preview: string | null | undefined): string | null {
  if (!preview) return null;
  if (preview === "[Vídeo]" || preview === "[Video]") return "🎥 Vídeo";
  if (preview === "[Imagem]" || preview === "[imagem]") return "📷 Imagem";
  return preview;
}

export function rowToInboxPatch(row: Record<string, unknown>): ConversationInboxRowPatch | null {
  const conversationId = typeof row.id === "string" ? row.id : null;
  if (!conversationId) return null;

  return {
    conversationId,
    lastMessagePreview: (row.last_message_preview as string | null) ?? undefined,
    lastMessageAt: (row.last_message_at as string | null) ?? undefined,
    lastMessageDirection: (row.last_message_direction as string | null) ?? undefined,
    unreadCount: typeof row.unread_count === "number" ? row.unread_count : undefined,
    updatedAt: typeof row.updated_at === "string" ? row.updated_at : undefined,
    state: typeof row.state === "string" ? row.state : undefined,
    isClosed: typeof row.is_closed === "boolean" ? row.is_closed : undefined,
    escalatedByAgentAt: (row.escalated_by_agent_at as string | null) ?? undefined,
    externalUserId: (row.external_user_id as string | null) ?? undefined,
    version: typeof row.version === "number" ? row.version : undefined,
  };
}

export function applyConversationInboxPatch(
  conv: MappedConversation,
  patch: ConversationInboxRowPatch,
  readTimestamps: Map<string, string>,
): MappedConversation | null {
  if (conv.conversation_id !== patch.conversationId) return null;

  if (
    typeof patch.version === "number" &&
    typeof conv.version === "number" &&
    patch.version < conv.version
  ) {
    return null;
  }

  const next: MappedConversation = { ...conv };
  let changed = false;

  if (patch.lastMessagePreview !== undefined) {
    const body = normalizePreviewBody(patch.lastMessagePreview);
    if (next.lastMessageBody !== body) {
      next.lastMessageBody = body;
      changed = true;
    }
  }

  if (patch.lastMessageAt !== undefined) {
    const at = patch.lastMessageAt || patch.updatedAt || next.lastMessageAt;
    if (at && next.lastMessageAt !== at) {
      next.lastMessageAt = at;
      changed = true;
    }
  }

  if (patch.lastMessageDirection !== undefined) {
    const fromUser =
      patch.lastMessageDirection === "outbound"
        ? "assistant"
        : patch.externalUserId ?? next.lastMessageFrom ?? "user";
    if (next.lastMessageFrom !== fromUser) {
      next.lastMessageFrom = fromUser;
      changed = true;
    }
  }

  if (patch.unreadCount !== undefined || patch.lastMessageAt !== undefined) {
    const unread = effectiveConversationUnreadCount(
      conv.conversation_id,
      patch.unreadCount ?? conv.unreadCount ?? 0,
      patch.lastMessageAt ?? conv.lastMessageAt ?? null,
      readTimestamps,
    );
    if (next.unreadCount !== unread || next.isUnread !== unread > 0) {
      next.unreadCount = unread;
      next.isUnread = unread > 0;
      changed = true;
    }
  }

  if (patch.updatedAt !== undefined && next.updated_at !== patch.updatedAt) {
    next.updated_at = patch.updatedAt;
    changed = true;
  }

  if (patch.version !== undefined && next.version !== patch.version) {
    next.version = patch.version;
    changed = true;
  }

  if (patch.state !== undefined && next.state !== patch.state) {
    next.state = patch.state;
    changed = true;
  }

  if (patch.isClosed !== undefined && next.is_closed !== patch.isClosed) {
    next.is_closed = patch.isClosed;
    changed = true;
  }

  if (
    patch.escalatedByAgentAt !== undefined &&
    next.escalated_by_agent_at !== patch.escalatedByAgentAt
  ) {
    next.escalated_by_agent_at = patch.escalatedByAgentAt;
    changed = true;
  }

  return changed ? next : null;
}

export function reorderConversationsByLastMessage(
  conversations: MappedConversation[],
): MappedConversation[] {
  return [...conversations].sort((a, b) => {
    const aTime = a.lastMessageAt || a.updated_at;
    const bTime = b.lastMessageAt || b.updated_at;
    return bTime.localeCompare(aTime);
  });
}

export function patchConversationListFromUpdate(
  conversations: MappedConversation[],
  row: Record<string, unknown>,
  readTimestamps: Map<string, string>,
): { next: MappedConversation[]; changed: boolean } {
  const patch = rowToInboxPatch(row);
  if (!patch) return { next: conversations, changed: false };

  let changed = false;
  const patched = conversations.map((conv) => {
    const updated = applyConversationInboxPatch(conv, patch, readTimestamps);
    if (updated) {
      changed = true;
      return updated;
    }
    return conv;
  });

  if (!changed) return { next: conversations, changed: false };

  return {
    next: reorderConversationsByLastMessage(patched),
    changed: true,
  };
}

export function dispatchConversationInboxBadgeSync(detail?: { conversationId?: string }) {
  window.dispatchEvent(new CustomEvent(CONVERSATION_INBOX_PATCH_EVENT, { detail }));
}

export function dispatchConversationInboxRowPatch(patch: ConversationInboxRowPatch) {
  window.dispatchEvent(
    new CustomEvent<ConversationInboxRowPatch>(CONVERSATION_INBOX_ROW_PATCH_EVENT, {
      detail: patch,
    }),
  );
}
